import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { 
  Users, Plus, Search, Mail, Phone, Edit2, Trash2, 
  UserPlus, Key, Shield, User, School, ChevronLeft, X, Check, CheckSquare
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showDeleteUser, setShowDeleteUser] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [processingBulk, setProcessingBulk] = useState(false);
  const [classes, setClasses] = useState([]);
  
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'parent'
  });
  
  const [studentForm, setStudentForm] = useState({
    student_name: '',
    birth_date: '',
    gender: '',
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    medical_conditions: '',
    address: '',
    class_ids: [],
    send_payment_email: true
  });
  
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchClasses();
  }, [roleFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (roleFilter) params.append('role', roleFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const res = await api.get(`/users?${params.toString()}`);
      setUsers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await api.get('/classes');
      setClasses(res.data || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.first_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // For staff/teacher, use invite flow
    if (userForm.role === 'teacher' || userForm.role === 'staff') {
      try {
        await api.post('/auth/staff/invite', {
          email: userForm.email,
          first_name: userForm.first_name,
          last_name: userForm.last_name || '',
          phone: userForm.phone || null,
          role: userForm.role
        });
        toast.success(`Invite sent to ${userForm.email}!`);
        setShowCreateUser(false);
        setUserForm({ email: '', password: '', first_name: '', last_name: '', phone: '', role: 'teacher' });
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to send invite');
      }
      return;
    }
    
    // For parent/admin, create directly with password
    if (!userForm.password) {
      toast.error('Password is required');
      return;
    }
    
    try {
      await api.post('/users', userForm);
      toast.success('User created successfully');
      setShowCreateUser(false);
      setUserForm({ email: '', password: '', first_name: '', last_name: '', phone: '', role: 'teacher' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleAddStudent = async () => {
    if (!studentForm.student_name || !studentForm.parent_email || !studentForm.parent_name) {
      toast.error('Please fill in student name, parent name and parent email');
      return;
    }
    
    try {
      const res = await api.post('/users/add-student', studentForm);
      toast.success(res.data.message || 'Student added successfully');
      setShowAddStudent(false);
      setStudentForm({
        student_name: '',
        birth_date: '',
        gender: '',
        parent_name: '',
        parent_email: '',
        parent_phone: '',
        medical_conditions: '',
        address: '',
        class_ids: [],
        send_payment_email: true
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add student');
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    try {
      await api.put(`/users/${selectedUser.id}`, {
        first_name: selectedUser.first_name,
        last_name: selectedUser.last_name,
        phone: selectedUser.phone,
        role: selectedUser.role
      });
      toast.success('User updated successfully');
      setShowEditUser(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllVisible = () => {
    const visibleIds = filteredUsers.map(u => u.id);
    setSelectedUsers(visibleIds);
  };

  const clearSelection = () => {
    setSelectedUsers([]);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setShowDeleteUser(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleBulkDelete = async () => {
    setProcessingBulk(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const userId of selectedUsers) {
      try {
        await api.delete(`/users/${userId}`);
        successCount++;
      } catch {
        failCount++;
      }
    }
    
    setProcessingBulk(false);
    toast.success(`Deleted ${successCount} user(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
    setSelectedUsers([]);
    setShowBulkDelete(false);
    fetchUsers();
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await api.post(`/users/${selectedUser.id}/reset-password`, { password: newPassword });
      toast.success('Password reset successfully');
      setShowResetPassword(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const toggleClassSelection = (classId) => {
    setStudentForm(prev => ({
      ...prev,
      class_ids: prev.class_ids.includes(classId)
        ? prev.class_ids.filter(id => id !== classId)
        : [...prev.class_ids, classId]
    }));
  };

  // Filter users based on search and role filter
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !roleFilter || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-500/20 text-red-400 border-red-500/30',
      staff: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      teacher: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      parent: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return styles[role] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="w-4 h-4" />;
      case 'staff': return <Users className="w-4 h-4" />;
      case 'teacher': return <School className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const totalPrice = studentForm.class_ids.reduce((sum, id) => {
    const cls = classes.find(c => c.id === id);
    return sum + (cls?.price || 0);
  }, 0);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="user-management-page">
      <Navbar />
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-gradient-to-b from-[#1a1a2e] to-[#0a0a0a] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-white/60 hover:text-white hover:bg-white/10"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF007F] to-purple-600 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                Manage Users
              </h1>
              <p className="text-white/60 mt-2">View and manage all user accounts</p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => setShowAddStudent(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="add-student-btn"
              >
                <School className="w-4 h-4 mr-2" />
                Add Student
              </Button>
              <Button 
                onClick={() => setShowCreateUser(true)}
                className="bg-[#FF007F] hover:bg-[#D6006A] text-white"
                data-testid="create-user-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white"
                  data-testid="search-input"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="h-10 bg-white/5 border border-white/10 text-white rounded-md px-3"
                data-testid="role-filter"
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="staff">Staff</option>
                <option value="teacher">Teacher</option>
                <option value="parent">Parent</option>
              </select>
              <Button type="submit" className="bg-[#FF007F] hover:bg-[#D6006A]" data-testid="search-btn">
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Bulk Selection Action Bar */}
        {selectedUsers.length > 0 && (
          <div className="mb-4 p-4 bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CheckSquare className="w-5 h-5 text-[#FF007F]" />
              <span className="text-white font-medium">{selectedUsers.length} user(s) selected</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={clearSelection}
                className="text-white/50 hover:text-white"
              >
                Clear
              </Button>
            </div>
            <Button
              onClick={() => setShowBulkDelete(true)}
              disabled={processingBulk}
              className="bg-red-500 hover:bg-red-600"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected
            </Button>
          </div>
        )}

        {/* Quick Select Actions */}
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={selectAllVisible}
            className="border-[#FF007F]/50 text-[#FF007F] hover:bg-[#FF007F]/10"
          >
            <CheckSquare className="w-4 h-4 mr-2" />
            Select All ({filteredUsers.length})
          </Button>
          {selectedUsers.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              className="text-white/50 hover:text-white"
            >
              Clear Selection
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {['admin', 'staff', 'teacher', 'parent'].map(role => (
            <Card key={role} className="bg-white/5 border-white/10">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-white/60 mb-1">
                  {getRoleIcon(role)}
                  <span className="capitalize">{role}s</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {users.filter(u => u.role === role).length}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Users List */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Users ({filteredUsers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12 text-white/50">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No users found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredUsers.map(user => {
                  const isSelected = selectedUsers.includes(user.id);
                  return (
                  <div 
                    key={user.id}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg gap-4 ${
                      isSelected ? 'bg-[#FF007F]/10 border border-[#FF007F]' : 'bg-white/5'
                    }`}
                    data-testid={`user-row-${user.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleUserSelection(user.id)}
                        className="border-white/30 data-[state=checked]:bg-[#FF007F] data-[state=checked]:border-[#FF007F]"
                      />
                      <div className="w-10 h-10 bg-[#FF007F]/20 rounded-full flex items-center justify-center">
                        {getRoleIcon(user.role)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-semibold">
                            {user.first_name} {user.last_name}
                          </span>
                          <Badge className={getRoleBadge(user.role)}>
                            {user.role}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-white/50 text-sm mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </span>
                          {user.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {user.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedUser({...user}); setShowEditUser(true); }}
                        className="border-white/10 text-white hover:bg-white/10"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedUser(user); setShowResetPassword(true); }}
                        className="border-white/10 text-white hover:bg-white/10"
                        data-testid={`reset-password-${user.id}`}
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedUser(user); setShowDeleteUser(true); }}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteUser} onOpenChange={setShowDeleteUser}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-white/70">
              Are you sure you want to delete <strong className="text-white">{selectedUser?.first_name} {selectedUser?.last_name}</strong>?
            </p>
            {selectedUser?.role === 'parent' && (
              <p className="text-yellow-400 text-sm mt-2">This will also delete their children and bookings.</p>
            )}
            <p className="text-red-400 text-sm mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteUser(false)} className="border-white/10 text-white">
              Cancel
            </Button>
            <Button onClick={handleDeleteUser} className="bg-red-500 hover:bg-red-600 text-white">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={showBulkDelete} onOpenChange={setShowBulkDelete}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Delete Users
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-white/70">
              Are you sure you want to delete <strong className="text-white">{selectedUsers.length} user(s)</strong>?
            </p>
            <p className="text-yellow-400 text-sm mt-2">Parent accounts will have their children and bookings deleted too.</p>
            <p className="text-red-400 text-sm mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDelete(false)} className="border-white/10 text-white">
              Cancel
            </Button>
            <Button onClick={handleBulkDelete} disabled={processingBulk} className="bg-red-500 hover:bg-red-600 text-white">
              <Trash2 className="w-4 h-4 mr-2" />
              {processingBulk ? 'Deleting...' : `Delete ${selectedUsers.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#FF007F]" />
              {userForm.role === 'parent' || userForm.role === 'admin' ? 'Create New User' : 'Invite Staff/Teacher'}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {userForm.role === 'parent' || userForm.role === 'admin' 
                ? 'Create a new user account with specified role'
                : 'Send an invite email - they will set their own password'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Role Selection First */}
            <div>
              <label className="text-sm text-white/70 mb-1 block">Role *</label>
              <select
                value={userForm.role}
                onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                className="w-full h-10 bg-white/5 border border-white/10 text-white rounded-md px-3"
                data-testid="create-role-select"
              >
                <option value="teacher">Teacher</option>
                <option value="staff">Staff</option>
                <option value="parent">Parent</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/70 mb-1 block">First Name *</label>
                <Input
                  value={userForm.first_name}
                  onChange={(e) => setUserForm({...userForm, first_name: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="create-firstname-input"
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Last Name</label>
                <Input
                  value={userForm.last_name}
                  onChange={(e) => setUserForm({...userForm, last_name: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="create-lastname-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-white/70 mb-1 block">Email *</label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="user@example.com"
                data-testid="create-email-input"
              />
            </div>

            <div>
              <label className="text-sm text-white/70 mb-1 block">Phone (Optional)</label>
              <Input
                value={userForm.phone}
                onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="07700 900000"
                data-testid="create-phone-input"
              />
            </div>

            {/* Password only for parent/admin */}
            {(userForm.role === 'parent' || userForm.role === 'admin') && (
              <div>
                <label className="text-sm text-white/70 mb-1 block">Password *</label>
                <Input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Min 6 characters"
                  data-testid="create-password-input"
                />
              </div>
            )}

            {/* Info box for staff/teacher */}
            {(userForm.role === 'teacher' || userForm.role === 'staff') && (
              <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20 text-sm">
                <p className="text-purple-300">
                  <Mail className="w-4 h-4 inline mr-2" />
                  An invite email will be sent. They will set their password and upload a profile photo when they accept.
                </p>
                <p className="text-purple-300/70 text-xs mt-2">
                  Invite expires in 14 days.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)} className="border-white/10 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button onClick={handleCreateUser} className="bg-[#FF007F] hover:bg-[#D6006A]" data-testid="confirm-create-btn">
              {(userForm.role === 'teacher' || userForm.role === 'staff') ? 'Send Invite' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Student Dialog */}
      <Dialog open={showAddStudent} onOpenChange={setShowAddStudent}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <School className="w-5 h-5 text-purple-400" />
              Add New Student
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Add a student and send payment email to their parent
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-purple-400" />
                Student Details
              </h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Student Name *</label>
                  <Input
                    value={studentForm.student_name}
                    onChange={(e) => setStudentForm({...studentForm, student_name: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="Student's full name"
                    data-testid="student-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Date of Birth</label>
                  <Input
                    type="date"
                    value={studentForm.birth_date}
                    onChange={(e) => setStudentForm({...studentForm, birth_date: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="student-dob-input"
                    min={`${new Date().getFullYear() - 75}-01-01`}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Gender</label>
                  <select
                    value={studentForm.gender}
                    onChange={(e) => setStudentForm({...studentForm, gender: e.target.value})}
                    className="w-full h-10 bg-white/5 border border-white/10 text-white rounded-md px-3"
                    data-testid="student-gender-select"
                  >
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Medical Conditions</label>
                  <Input
                    value={studentForm.medical_conditions}
                    onChange={(e) => setStudentForm({...studentForm, medical_conditions: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="Allergies, conditions..."
                    data-testid="student-medical-input"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-400" />
                Parent/Guardian Details
              </h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Parent Name *</label>
                  <Input
                    value={studentForm.parent_name}
                    onChange={(e) => setStudentForm({...studentForm, parent_name: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="Parent's full name"
                    data-testid="parent-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Parent Email *</label>
                  <Input
                    type="email"
                    value={studentForm.parent_email}
                    onChange={(e) => setStudentForm({...studentForm, parent_email: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="parent@example.com"
                    data-testid="parent-email-input"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Parent Phone</label>
                  <Input
                    value={studentForm.parent_phone}
                    onChange={(e) => setStudentForm({...studentForm, parent_phone: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="07700 900000"
                    data-testid="parent-phone-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Address</label>
                  <Input
                    value={studentForm.address}
                    onChange={(e) => setStudentForm({...studentForm, address: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="Full address"
                    data-testid="student-address-input"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-[#FF007F]/10 rounded-lg border border-[#FF007F]/20">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <School className="w-4 h-4 text-[#FF007F]" />
                Select Classes
              </h4>
              <div className="grid sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {classes.length === 0 ? (
                  <p className="text-white/50 col-span-2 text-center py-4">No classes available</p>
                ) : (
                  classes.map(cls => (
                    <div 
                      key={cls.id}
                      onClick={() => toggleClassSelection(cls.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        studentForm.class_ids.includes(cls.id)
                          ? 'bg-[#FF007F]/20 border-[#FF007F]'
                          : 'bg-white/5 border-white/10 hover:border-white/30'
                      }`}
                      data-testid={`class-option-${cls.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium text-sm">{cls.name}</p>
                          <p className="text-white/50 text-xs">{cls.category}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[#FF007F] font-bold">£{cls.price?.toFixed(2)}</span>
                          {studentForm.class_ids.includes(cls.id) && (
                            <Check className="w-4 h-4 text-[#FF007F]" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {studentForm.class_ids.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                  <span className="text-white/70">Total:</span>
                  <span className="text-[#FF007F] font-bold text-xl">£{totalPrice.toFixed(2)}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
              <input
                type="checkbox"
                checked={studentForm.send_payment_email}
                onChange={(e) => setStudentForm({...studentForm, send_payment_email: e.target.checked})}
                className="w-4 h-4 rounded border-white/20"
                id="send-email"
                data-testid="send-email-checkbox"
              />
              <label htmlFor="send-email" className="text-white/70 text-sm">
                Send payment/registration email to parent
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStudent(false)} className="border-white/10 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button onClick={handleAddStudent} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-add-student-btn">
              Add Student
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-[#FF007F]" />
              Edit User
            </DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white/70 mb-1 block">First Name</label>
                  <Input
                    value={selectedUser.first_name || ''}
                    onChange={(e) => setSelectedUser({...selectedUser, first_name: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="edit-firstname-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Last Name</label>
                  <Input
                    value={selectedUser.last_name || ''}
                    onChange={(e) => setSelectedUser({...selectedUser, last_name: e.target.value})}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="edit-lastname-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Phone</label>
                <Input
                  value={selectedUser.phone || ''}
                  onChange={(e) => setSelectedUser({...selectedUser, phone: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="edit-phone-input"
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Role</label>
                <select
                  value={selectedUser.role}
                  onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value})}
                  className="w-full h-10 bg-white/5 border border-white/10 text-white rounded-md px-3"
                  data-testid="edit-role-select"
                >
                  <option value="parent">Parent</option>
                  <option value="teacher">Teacher</option>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUser(false)} className="border-white/10 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button onClick={handleEditUser} className="bg-[#FF007F] hover:bg-[#D6006A]" data-testid="confirm-edit-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-400" />
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Set a new password for {selectedUser?.first_name} {selectedUser?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-sm text-white/70 mb-1 block">New Password *</label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              placeholder="Min 6 characters"
              data-testid="new-password-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowResetPassword(false); setNewPassword(''); }} className="border-white/10 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button onClick={handleResetPassword} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-reset-btn">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
